class HeaderElement extends HTMLElement {
  static observedAttributes = ['data-sticky'];

  constructor() {
    super();
    this.state = {
      lastScrollTop: 0,
      isHidden: false,
      headerHeight: 0,
      announcementHeight: 0,
      productFeedInView: false,
    };

    this.config = {
      threshold: 100,
      slideDuration: 300,
      hiddenTransform: 'translateY(-160px)',
    };

    this.observers = [];
  }

  connectedCallback() {
    this.setupDOMReferences();
    if (!this.isSticky || !this.header) return;

    this.setupHeaderOffset();
    this.updateHeaderHeight();
    this.setupEventListeners();
    this.connectObservers();
    this.setupInitialStickyState();
  }

  disconnectedCallback() {
    if (!this.isSticky) return;
    this.removeEventListeners();
    this.disconnectObservers();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'data-sticky') {
      this.isSticky = newValue === 'true';
    }
  }

  setupDOMReferences() {
    this.header = this.closest('.header-elements');
    this.announcementBar = this.querySelector('.js-announcement-bar');
    this.productFeed = document.querySelector('product-feed');
    this.isSticky = this.dataset.sticky === 'true';
  }

  setupHeaderOffset() {
    const setHeaderOffset = () => {
      const headerElements = document.querySelectorAll('header-element');
      if (!headerElements.length) return;

      const headerHeight = [...headerElements].reduce((acc, el) => acc + el.offsetHeight, 0);
      document.body.style.setProperty('--header-offset', `${headerHeight + 2}px`);
    };

    setHeaderOffset();
    window.addEventListener('resize', setHeaderOffset);
  }

  setupInitialStickyState() {
    this.headerOffset = document.createElement('div');
    this.headerOffset.style.height = `${this.state.headerHeight}px`;
    this.headerOffset.className = 'absolute inset-0 bg-background1 shopify-section-group-header-group';
    this.headerOffset.setAttribute('aria-hidden', 'true');
    this.header.before(this.headerOffset);

    this.header.classList.add(
      'transition-top',
      'duration-300',
      'sticky',
      'z-[1010]',
      '-top-[var(--header-offset)]',
      'bg-transparent'
    );
  }

  setupEventListeners() {
    this.handleScroll = this.handleScroll.bind(this);
    this.updateHeaderHeight = this.updateHeaderHeight.bind(this);

    window.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.updateHeaderHeight);
  }

  removeEventListeners() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.updateHeaderHeight);
  }

  connectObservers() {
    this.setupProductFeedInViewStateObserver();
  }

  disconnectObservers() {
    this.observers.forEach(observer => observer.disconnect());
  }

  updateHeaderHeight() {
    this.state.headerHeight = this.header.offsetHeight;
    this.state.announcementHeight = this.announcementBar?.offsetHeight ?? 0;
    if (this.headerOffset) this.headerOffset.style.height = `${this.state.headerHeight}px`;
  }

  handleScroll() {
    const scrollPosition = window.scrollY || document.documentElement.scrollTop;
    this.handleHeaderVisibility(scrollPosition);
  }

  setupProductFeedInViewStateObserver() {
    if (!this.productFeed) return;

    const observer = new IntersectionObserver(
      entries => {
        this.state.productFeedInView = entries[0].isIntersecting;
      }
    );

    observer.observe(this.productFeed);
    this.observers.push(observer);
  }

  handleHeaderVisibility(scrollPosition) {
    if (this.state.productFeedInView) {
      this.hideHeader();
    } else if (scrollPosition > this.config.threshold) {
      if (scrollPosition > this.state.lastScrollTop && !this.state.isHidden) {
        this.hideHeader();
      } else if (scrollPosition < this.state.lastScrollTop && this.state.isHidden) {
        this.showHeader();
      }
    } else if (scrollPosition <= this.state.announcementHeight) {
      this.resetHeaderPosition();
    } else if (this.state.isHidden) {
      this.showHeader();
    }

    this.state.lastScrollTop = scrollPosition;
  }

  hideHeader() {
    this.resetHeaderPosition();
    this.state.isHidden = true;
  }

  isMobile() {
    const mobileBreakpoints = ['xs', 'sm', 'md'];
    const currentBreakpoint = getCurrentBreakpoint()
    return mobileBreakpoints.includes(currentBreakpoint);
  }

  showHeader() {
    const isMobile = this.isMobile();
    this.header.style.top = isMobile ? `-${this.state.announcementHeight}px` : `-${this.state.announcementHeight - 2}px`;
    this.state.isHidden = false;
  }

  resetHeaderPosition() {
    this.header.style.top = '';
  }
}

customElements.define('header-element', HeaderElement);
class LocalizationForm extends HTMLElement {
  constructor() {
    super();
    this.elements = {
      input: this.querySelector(
        'input[name="language_code"],input[name="locale_code"], input[name="country_code"]'
      ),
    };
    this.querySelectorAll("a").forEach((item) =>
      item.addEventListener("click", this.onItemClick.bind(this))
    );
  }

  onItemClick(event) {
    event.preventDefault();
    const form = this.querySelector("form");
    this.elements.input.value = event.currentTarget.dataset.value;
    if (form) form.submit();
  }
}

customElements.define("localization-form", LocalizationForm);

// fix childMenu positions
const fixChildMenuPositions = () => {
  const childMenus = document.querySelectorAll("._headerChildMenu");
  const isRTL = document.body.style.direction === "rtl";
  const windowWidth = window.innerWidth;

  childMenus.forEach((childMenu) => {
    let isOffScreen = false;
    const childMenuRect = childMenu.getBoundingClientRect();

    // Check if the child menu itself is off-screen
    if (isRTL) {
      isOffScreen = childMenuRect.left < 0;
    } else {
      isOffScreen = childMenuRect.right > windowWidth;
    }

    // If child menu is not off-screen, check its grandchild menus
    if (!isOffScreen) {
      const grandChildMenus = childMenu.querySelectorAll("._headerGrandChildMenu");
      grandChildMenus.forEach((grandChildMenu) => {
        // Temporarily make the grandchild menu visible for accurate measurements
        const originalDisplay = grandChildMenu.style.display;
        grandChildMenu.style.display = 'block';
        grandChildMenu.style.visibility = 'hidden';

        const grandChildMenuRect = grandChildMenu.getBoundingClientRect();

        // Restore original display
        grandChildMenu.style.display = originalDisplay;
        grandChildMenu.style.visibility = '';

        if (isRTL) {
          if (grandChildMenuRect.left < 0) isOffScreen = true;
        } else {
          if (grandChildMenuRect.right > windowWidth) isOffScreen = true;
        }
      });
    }

    if (isOffScreen) {
      childMenu.setAttribute("data-offscreen", "true");
    } else {
      childMenu.removeAttribute("data-offscreen");
    }
  });};

window.addEventListener("resize", fixChildMenuPositions);
window.addEventListener("DOMContentLoaded", fixChildMenuPositions);

// Search
